import { HttpClient } from '@angular/common/http';
import { Component, OnInit, computed, signal } from '@angular/core';
import { environment } from 'src/environments/environment';
import NiboWhatsAppConnection from '../../models/nibo-whatsapp-connection';
import { IncomingNiboWhatsAppData } from '../../models/incoming-nibo-whatsapp-data';
import { MatDialog } from '@angular/material/dialog';
import { MsalService } from '@azure/msal-angular';
import { ToastrService } from 'ngx-toastr';
import { ConnectionConfirmDeleteComponent } from '../connection-confirm-delete/connection-confirm-delete.component';
import { WhatsAppConnectionStatus } from '../../models/whatsapp-connection-status';
import Tenant from '../../models/tenant';
import { ConnectionConfirmRecreateComponent } from '../connection-confirm-recreate/connection-confirm-recreate.component';
import { ConnectionScreenshotComponent } from '../connection-screenshot/connection-screenshot.component';
import { ConnectionConfirmRestartComponent } from '../connection-confirm-restart/connection-confirm-restart.component';

export class ConnectionStatusFilterConfig {
  constructor(public checked: boolean,
    public readonly label: string,
    public readonly value: WhatsAppConnectionStatus) { }
}

export class ConnectionTenantFilterConfig {
  constructor(public checked: boolean, public value: Tenant) { }
}

export class ConnectionInfrastructureFilterConfig {
  constructor(public checked: boolean, readonly label: string, readonly value: 'databaseMiss' | 'podMiss') { }
}

@Component({
  standalone: false,
  selector: 'app-connections-list',
  templateUrl: './connections-list.component.html',
  styleUrls: ['./connections-list.component.css']
})
export class ConnectionsListComponent implements OnInit {

  // Signals: escritos dentro do subscribe do HTTP e lidos no template. Sob change
  // detection zoneless, atribuir a um campo comum num callback assincrono nao agenda
  // renderizacao -- a tela ficaria presa no spinner mesmo com a resposta em maos.
  public readonly connections = signal<NiboWhatsAppConnection[]>([]);
  public readonly loading = signal(true);
  public readonly refreshing = signal(false);
  public readonly lastUpdate = signal('');

  public searchBarContent!: string;

  public connectionStatusFilters = [
    new ConnectionStatusFilterConfig(true, 'Online', WhatsAppConnectionStatus.Online),
    new ConnectionStatusFilterConfig(true, 'Offline', WhatsAppConnectionStatus.Offline),
    new ConnectionStatusFilterConfig(true, 'QRCode', WhatsAppConnectionStatus.WaitingForQrCodeScan),
    new ConnectionStatusFilterConfig(true, 'Inicializando', WhatsAppConnectionStatus.Initializing)
  ];

  public connectionTenantFilters = [
    new ConnectionTenantFilterConfig(true, Tenant.accountantTenant),
    new ConnectionTenantFilterConfig(true, Tenant.salesTenant),
    new ConnectionTenantFilterConfig(true, Tenant.organizationTenant)
  ];

  public connectionInfraFilters = [
    new ConnectionInfrastructureFilterConfig(true, 'Mostrar conexões que não estão no banco de dados', 'databaseMiss'),
    new ConnectionInfrastructureFilterConfig(true, 'Mostrar conexões que não possuem infraestrutura criada', 'podMiss')
  ];

  private static readonly SCREENSHOT_ALLOWED_EMAILS = [
    'alvaro.moura@nibo.com.br',
    'rafael.heringer@nibo.com.br',
    'leonardo.farias@nibo.com.br',
    'rafael@nibo.com.br',
    'shayene.grechi@nibo.com.br'
  ];

  constructor(private readonly httpClient: HttpClient,
    private readonly dialog: MatDialog,
    private readonly toastrService: ToastrService,
    private readonly authService: MsalService) { }

  // computed em vez de getter: um getter no template e reavaliado a cada ciclo de change
  // detection, e cada avaliacao fazia uma leitura no cache do MSAL. A conta autenticada
  // nao muda sem recarregar a pagina, entao o computed calcula uma vez e memoiza.
  public readonly canTakeScreenshot = computed(() => {
    const account = this.authService.instance.getActiveAccount()
      ?? this.authService.instance.getAllAccounts()[0];

    const email = account?.username?.toLowerCase();

    return !!email &&
      ConnectionsListComponent.SCREENSHOT_ALLOWED_EMAILS.includes(email);
  });

  ngOnInit(): void {
    this.loadConnections();
  }

  public loadConnections(): void {
    this.refreshing.set(true);
    this.httpClient.get<IncomingNiboWhatsAppData[]>(
      `${environment.whatsAppApi}/infrastructure/connections`
    ).subscribe({
      next: (data: IncomingNiboWhatsAppData[]) => {
        const mapped = data
          .map(d => new NiboWhatsAppConnection(d))
          .sort((a, b) => {
            if (!a.pod || !b.pod) return 1000;

            const order = [
              WhatsAppConnectionStatus.Online,
              WhatsAppConnectionStatus.WaitingForQrCodeScan,
              WhatsAppConnectionStatus.Initializing,
              WhatsAppConnectionStatus.Offline
            ];

            return order.indexOf(a.connection?.whatsAppStatus!) -
                   order.indexOf(b.connection?.whatsAppStatus!);
          });

        this.connections.set(mapped);
        this.loading.set(false);
        this.refreshing.set(false);
        this.lastUpdate.set(Date());
      },
      error: () => {
        this.loading.set(false);
        this.refreshing.set(false);
      }
    });
  }

  private deleteConnection(connection: NiboWhatsAppConnection) {
    this.httpClient.delete(`${environment.whatsAppApi}/connections/${connection.connectionId}`).subscribe({
      next: () => {
        this.toastrService.success(`A conexão ${connection.connectionId} foi deletada com sucesso`);
      }
    });
  }

  private recreateConnection(connection: NiboWhatsAppConnection) {
    this.httpClient.post(`${environment.whatsAppApi}/connections/${connection.connectionId}/recreate`, {}).subscribe({
      next: () => {
        this.toastrService.success(`A conexão ${connection.connectionId} foi recriada com sucesso`);
      }
    });
  }

  private restartConnection(connection: NiboWhatsAppConnection) {
    this.httpClient.delete(`${environment.whatsAppApi}/infrastructure/connections/${connection.connectionId}/pod`).subscribe({
      next: () => {
        this.toastrService.success(`A conexão ${connection.connectionId} está sendo reiniciada`);
      }
    });
  }

  public openLogs(connection: NiboWhatsAppConnection) {
    const url = `https://app.datadoghq.com/logs?query=service%3A%28waha%20OR%20nibo-waha%29%20connection_id%3A${connection.connectionId}&agg_m=count&agg_m_source=base&agg_t=count&cols=host%2Cservice&fromUser=true&messageDisplay=inline&refresh_mode=sliding&storage=hot&stream_sort=desc&viz=stream&live=true`;
    window.open(url, '_blank');
  }

  public openScreenshot(connection: NiboWhatsAppConnection) {
    if (!this.canTakeScreenshot) {
      this.toastrService.warning(
        `Você não tem permissão para tirar screenshot`
      );
      return;
    }

    this.dialog.open(ConnectionScreenshotComponent, {
      data: { connection },
      maxWidth: '90vw'
    });
  }

  public openConfirmRestart(connection: NiboWhatsAppConnection) {
    this.dialog.open(ConnectionConfirmRestartComponent, {
      data: { connection }
    }).afterClosed().subscribe({
      next: (confirm: boolean) => {
        if (confirm) {
          this.restartConnection(connection);
        }
      }
    })
  }

  public openConfirmRecreate(connection: NiboWhatsAppConnection) {
    this.dialog.open(ConnectionConfirmRecreateComponent, {
      data: { connection }
    }).afterClosed().subscribe({
      next: (confirm: boolean) => {
        if (confirm) {
          this.recreateConnection(connection);
        }
      }
    })
  }

  public openConfirmDelete(connection: NiboWhatsAppConnection) {
    this.dialog.open(ConnectionConfirmDeleteComponent, {
      data: { connection }
    }).afterClosed().subscribe({
      next: (confirm: boolean) => {
        if (confirm) {
          this.deleteConnection(connection);
        }
      }
    })
  }

  public async copyConnectionId(connectionId: string) {
    try {
      await navigator.clipboard.writeText(connectionId);
      this.toastrService.info('Id da conexão copiado');
    }
    catch { }
  }
}
